import { roundToTwoDecimals, percentChange } from '../../Utils/decimalUtils';
import { applyAdjustmentSpec } from './relativePriceInput';
import type { AdjustmentSpec, AdjustmentValue, TierAdjustmentValue } from './PriceSimulation.types';
import { isTiered } from './priceSimulationModel';

export interface BulkApplyPlan {
    id: string;
    pricingScheme: number;
    currentPrice: number;
    breakpoints: { index: number; currentUnitPrice: number; isIncluded: boolean }[];
}

export const adjustPrice = (current: number, spec: AdjustmentSpec): { percentage: number; newPrice: number } => {
    const newPrice = applyAdjustmentSpec(current, spec);
    if (newPrice === current) return { percentage: 0, newPrice };
    return { percentage: spec.kind === 'percent' ? spec.percent : percentChange(current, newPrice), newPrice };
};

export const applySpecToPlanEntry = (entries: Record<string, AdjustmentValue>, plan: BulkApplyPlan, spec: AdjustmentSpec | null, includedPrice: number | null): void => {
    if (isTiered(plan)) {
        const existing = entries[plan.id] ?? { percentage: 0, newPrice: 0 };
        const tiers: Record<number, TierAdjustmentValue> = { ...existing.tiers };
        plan.breakpoints.forEach((bp) => {
            if (bp.isIncluded) {
                if (includedPrice !== null) {
                    tiers[bp.index] = { percentage: 0, newUnitPrice: roundToTwoDecimals(includedPrice) };
                }
                return;
            }
            if (spec === null) return;
            const { percentage, newPrice } = adjustPrice(bp.currentUnitPrice, spec);
            tiers[bp.index] = { percentage, newUnitPrice: newPrice };
        });
        entries[plan.id] = { percentage: existing.percentage, newPrice: existing.newPrice, tiers };
    } else {
        if (plan.currentPrice === 0) {
            if (includedPrice !== null) entries[plan.id] = { percentage: 0, newPrice: roundToTwoDecimals(includedPrice) };
            return;
        }
        if (spec === null) return;
        entries[plan.id] = adjustPrice(plan.currentPrice, spec);
    }
};

export const applyKeptToBreakpoint = (entries: Record<string, AdjustmentValue>, planId: string, breakpoint: BulkApplyPlan['breakpoints'][number], isKept: boolean): void => {
    const existing = entries[planId] ?? { percentage: 0, newPrice: 0 };
    const tiers = { ...existing.tiers, [breakpoint.index]: { percentage: 0, newUnitPrice: breakpoint.currentUnitPrice, isKept } };
    entries[planId] = { percentage: existing.percentage, newPrice: existing.newPrice, tiers };
};

export const applyKeptToPlanEntry = (entries: Record<string, AdjustmentValue>, plan: BulkApplyPlan, isKept: boolean): void => {
    if (isTiered(plan)) {
        plan.breakpoints.forEach((bp) => applyKeptToBreakpoint(entries, plan.id, bp, isKept));
        return;
    }
    entries[plan.id] = { percentage: 0, newPrice: plan.currentPrice, isKept };
};
